















































































































import { Component, Vue, Watch } from "vue-property-decorator";
import QuickSnack from "@/components/QuickSnack.vue";
import UserEditDialog from "@/components/UserEditDialog.vue";
import UserAuditDialog from "@/components/UserAuditDialog.vue";
import UserImport from "@/components/UserImport.vue";
import { emptyCompany, ListUser } from "@/domain/types/user_manager";
import {
  domain,
  Company,
  Branch,
  EditUser,
  UserQueryFilter,
  User,
  PERMISSION_ATTRS,
  PartialPermission,
  ClientPermission,
  VueRoot,
  ExcelUser
} from "@/domain";
import * as util from "@/util";
import * as uiUtil from "@/uiUtil";
import { exportUsersExcel } from "@/domain/users_export";

@Component({
  components: { QuickSnack, UserEditDialog, UserAuditDialog, UserImport }
})
export default class UserManager extends Vue {
  isLoading = false;

  // Table properties
  filters?: UserQueryFilter = {
    queryAll: false
  };
  users: ListUser[] = [];
  userSearch = "";

  get lang() {
    return this.$store.state.lang.data;
  }

  get currentCompany(): Company | null {
    if (this.$store.state.selectedCompanyLoading) {
      return null;
    }
    return this.$store.state.selectedCompany;
  }

  get userCompanies(): Company[] {
    if (!this.$store.state.userCompanies) {
      return [];
    }
    return this.$store.state.userCompanies;
  }

  get currentUser(): User {
    return this.$store.state.currentUser || {};
  }

  get currentBranches(): Branch[] {
    return uiUtil.branchComboItems(this.currentCompany, this.userPermission);
  }

  get userPermission(): PartialPermission {
    return this.$store.getters.userPermission;
  }

  get availablePermissionNames(): string[] {
    return PERMISSION_ATTRS.map((attr) => attr.toString());
  }

  get isMobile(): boolean {
    return (this.$root as VueRoot).isMobile;
  }

  get isCompanyAdmin() {
    return this.currentUser.isToken || this.currentUser.isGlobalAdmin;
  }

  @Watch("currentCompany")
  onCompanyChanged(newCompany?: Company, oldCompany?: Company) {
    if (!newCompany || (oldCompany && newCompany.id === oldCompany.id)) {
      return;
    }
    if (newCompany) {
      this.refreshUsers();
    }
  }

  created() {
    if (this.userPermission.isGlobalAdmin && !this.currentCompany) {
      return;
    }
    this.$nextTick(() => {
      this.refreshUsers();
    });
  }

  get userEditDialog(): UserEditDialog {
    return this.$refs.userEditDialog as UserEditDialog;
  }

  get userAuditDialog(): UserAuditDialog {
    return this.$refs.userAuditDialog as UserAuditDialog;
  }

  get snack(): QuickSnack {
    return this.$refs.snack as QuickSnack;
  }

  refreshUsers() {
    util.promiseEnd(this.asyncRefreshUsers());
  }

  permissionScore(perm: ClientPermission): number {
    if (perm.admin) {
      return 10;
    }
    let score = 0;
    PERMISSION_ATTRS.forEach((p) => (score += perm[p] ? 1 : 0));
    return score;
  }

  permissionList(perm: ListUser): string[] {
    if (perm.admin) {
      return [this.lang.administrator];
    }
    const permLabels: string[] = [];
    PERMISSION_ATTRS.forEach((p) => {
      if (perm[p]) {
        switch (p) {
          case "allowTxnStatistics":
            permLabels.push(this.lang.statistics);
            break;
          case "allowConnectionGraph":
            permLabels.push(this.lang.terminalsStatus);
            break;
          case "paymentLink":
            permLabels.push(this.lang.paymentLink2);
            break;
          case "clientAPI":
            permLabels.push(this.lang.clientAPI);
            break;
          case "txnModification":
            permLabels.push(this.lang.editTransactions);
            break;
          case "txnView":
            permLabels.push(this.lang.seeTransactions);
            break;
          case "vendingAdmin":
            permLabels.push(this.lang.vending2);
            break;
          case "topayAdmin":
            permLabels.push(this.lang.topay);
            break;
          default:
            permLabels.push(p);
            break;
        }
      }
    });
    if (permLabels.length === 0) {
      permLabels.push(this.lang.basicRole);
    }
    return permLabels;
  }

  cmdExportUsers() {
    const excelUsers: ExcelUser[] = [];
    this.users.forEach((u) => {
      excelUsers.push({
        displayName: u.displayName,
        email: u.email,
        permissionScore: this.getPermissionsString(u),
        branchIDs: this.itemBranches(u),
        lastSignInTimeUTC: u.lastSignInTimeUTC ?? ""
      });
    });
    exportUsersExcel(excelUsers);
  }

  getPermissionsString(u: ListUser) {
    let result = "";
    const perm = this.permissionList(u);
    perm.forEach((p) => {
      result += result.length > 0 ? ", " + p : p;
    });
    return result;
  }

  async asyncRefreshUsers() {
    const filter: UserQueryFilter = {
      queryAll: false
    };
    if (!this.currentCompany) {
      filter.queryAll = true;
    } else {
      filter.companyID = this.currentCompany.id;
    }
    this.isLoading = true;
    try {
      const users = await domain.backend.queryUsers(filter);
      const flattenedUsers = [];
      for (const user of users) {
        const allUsers: ListUser[] = [];
        for (const perm of user.permissions) {
          const company: Company = this.userCompanies.find((c) => c.id === perm.companyID) || emptyCompany();
          const mappedUser: ListUser = {
            ...user,
            ...perm,
            permissionScore: this.permissionScore(perm),
            company,
            companyName: company ? company.name : "-",
            companyID: perm.companyID || 0,
            allUsers,
            originalUser: user
          };
          allUsers.push(mappedUser);
          flattenedUsers.push(mappedUser);
        }
      }
      this.filters = filter;
      this.users = flattenedUsers;
    } catch (error) {
      util.logError("Error listando usuarios", error);
      this.users = [];
      this.snack.show(uiUtil.toErrorMsg(error as Error, this.lang.couldNotAccessUserList), "error");
    }
    this.isLoading = false;
    this.userSearch = "";
  }

  itemBranches(item: ListUser): string {
    if (!item.branchIDs || item.branchIDs.length === 0) {
      return this.lang.all2;
    }
    /*const company = this.getCompany(item.companyID);
    return item.branchIDs
      .map(branchID => {
        if (company && company.branches) {
          const branch = company.branches.find(b => b.id === branchID);
          if (branch) {
            return branch.name;
          }
        }
        return 'Oficina ' + branchID;
      })
      .join(', ');*/
    if (item.branchIDs.length > 5) {
      return `${item.branchIDs.length} oficinas`;
    }
    return (
      "Oficinas: " +
      item.branchIDs
        .map((branchID) => {
          return +branchID;
        })
        .join(", ")
    );
  }

  cmdNewItem() {
    this.userEditDialog.openForNewItem();
  }

  cmdImportUsers() {
    if (!this.currentCompany) {
      this.onDialogMessage({ message: this.lang.selectMErchantDialog, color: "red" });
      return;
    }
    (this.$refs.userImport as UserImport).open();
  }

  lstEditItem(item: ListUser) {
    this.userEditDialog.openForEdit(item);
  }

  lstAuditItem(item: ListUser) {
    return this.userAuditDialog.openForUser(item.uid);
  }

  onUserSaved() {
    this.refreshUsers();
  }

  onDialogMessage(content: { message: string; color: string }) {
    this.snack.show(content.message, content.color);
  }

  async lstDeleteItem(item: ListUser) {
    if (
      confirm(
        `${this.lang.removingAccessAlert1} ${item.email} ${this.lang.removingAccessAlert2} ${item.companyName} ${this.lang.removingAccessAlert3}`
      )
    ) {
      const deletedUser: EditUser = {
        edit: true,
        uid: item.uid,
        permissions: [
          {
            companyID: item.companyID,
            remove: true
          }
        ]
      };
      try {
        const editedUser = await domain.backend.saveUser(deletedUser);
        this.snack.show(this.lang.deletedUserRefresh, "success");
        util.log("User saved", editedUser);
        this.isLoading = false;
        this.refreshUsers();
      } catch (error) {
        util.logError("Error eliminando usuario", item, error);
        this.isLoading = false;
        this.snack.show(uiUtil.toErrorMsg(error as Error, this.lang.couldNotRemoveUser), "error");
      }
    }
  }

  get tableHeaders() {
    const headers: { text?: string; value: string; align: string; sortable?: boolean }[] = [];
    headers.push({
      text: this.lang.name,
      align: "left",
      value: "displayName"
    });
    headers.push({
      text: this.lang.email2,
      align: "center",
      value: "email"
    });
    if (this.filters && this.filters.queryAll) {
      headers.push({
        text: this.lang.merchant,
        align: "left",
        value: "companyName"
      });
    }
    headers.push({
      text: this.lang.permissions,
      align: "center",
      value: "permissionScore"
    });
    headers.push({ text: this.lang.office, value: "branchIDs", align: "center" });
    headers.push({
      text: this.lang.lastAccess,
      value: "lastSignInTimeUTC",
      align: "center"
    });
    headers.push({ value: "uid", align: "center", sortable: false });
    return headers;
  }
}
